/**
 * Cálculo de horarios disponibles en el cliente — usado por el futuro
 * portal público (DAF v2, sección 11) para mostrar solo horas que
 * probablemente se puedan reservar, ANTES de llamar al RPC
 * `crear_reserva_publica` que hace la validación real y definitiva en el
 * servidor (schema.sql, sección 14).
 *
 * Esta función nunca es la última palabra sobre si una hora está libre:
 * es una optimización de UI para no ofrecer horas obviamente inválidas.
 * La regla de negocio 1 (no doble reserva) la impone el `exclude using
 * gist` de la tabla `citas`, no este código.
 */

export interface Intervalo {
  inicio: Date;
  fin: Date;
}

interface CalcularSlotsParams {
  fecha: Date; // día para el que se calculan slots (se usa año/mes/día)
  horaInicioLaboral: string; // 'HH:MM'
  horaFinLaboral: string; // 'HH:MM'
  duracionServicioMinutos: number;
  citasExistentes: Intervalo[]; // citas activas de la profesional ese día
  bloqueos: Intervalo[]; // bloqueos de horario ese día
  pasoMinutos?: number; // granularidad de los slots ofrecidos, default 15
  ahora?: Date; // inyectable para tests; default new Date()
  /** Anticipación mínima configurable por salón (DAF v2 / recomendaciones
   * costo $0, sección 12: "se definirá una anticipación mínima
   * configurable para reservar"). Antes de esto solo se filtraban horas
   * ya pasadas — un slot a 2 minutos de "ahora" se ofrecía igual, algo
   * poco realista para un salón real. `crear_reserva_publica` aplica la
   * misma regla en el servidor (`salones.anticipacion_minima_minutos`);
   * este filtro es solo para no ofrecerle a la clienta en el cliente una
   * hora que el servidor va a rechazar. Default 0 = comportamiento
   * anterior sin cambios. */
  anticipacionMinimaMinutos?: number;
}

function seSuperponen(a: Intervalo, b: Intervalo): boolean {
  return a.inicio < b.fin && b.inicio < a.fin;
}

function horaEnFecha(fecha: Date, horaHHMM: string): Date {
  const [h, m] = horaHHMM.split(':').map(Number);
  const d = new Date(fecha);
  d.setHours(h, m, 0, 0);
  return d;
}

/**
 * Devuelve los intervalos [inicio, fin) de `duracionServicioMinutos` que
 * caben dentro del horario laboral, no chocan con citas existentes ni con
 * bloqueos, y no están en el pasado.
 */
export function calcularSlotsDisponibles(params: CalcularSlotsParams): Intervalo[] {
  const {
    fecha,
    horaInicioLaboral,
    horaFinLaboral,
    duracionServicioMinutos,
    citasExistentes,
    bloqueos,
    pasoMinutos = 15,
    ahora = new Date(),
    anticipacionMinimaMinutos = 0,
  } = params;

  const inicioLaboral = horaEnFecha(fecha, horaInicioLaboral);
  const finLaboral = horaEnFecha(fecha, horaFinLaboral);
  const ocupados = [...citasExistentes, ...bloqueos];
  const limiteMinimo = new Date(ahora.getTime() + anticipacionMinimaMinutos * 60_000);

  const slots: Intervalo[] = [];
  let cursor = new Date(inicioLaboral);

  while (cursor.getTime() + duracionServicioMinutos * 60_000 <= finLaboral.getTime()) {
    const candidato: Intervalo = {
      inicio: new Date(cursor),
      fin: new Date(cursor.getTime() + duracionServicioMinutos * 60_000),
    };

    const enElPasado = candidato.inicio <= limiteMinimo;
    const chocaConAlgo = ocupados.some((o) => seSuperponen(candidato, o));

    if (!enElPasado && !chocaConAlgo) {
      slots.push(candidato);
    }

    cursor = new Date(cursor.getTime() + pasoMinutos * 60_000);
  }

  return slots;
}
